







































import Vue from 'vue'
import debounce from 'lodash/debounce'
import sortedLastIndex from 'lodash/sortedLastIndex'
import { MultipleUnitsItem } from '@/model/index'

export default Vue.extend({
  props: {
    comKey: {
      type: [Number, String],
      required: false,
      default: undefined
    },
    quantity: {
      type: Number,
      required: true
    },
    minQuantity: {
      type: Number,
      required: true
    },
    maxQuantity: {
      type: Number,
      required: true
    },
    quantityUnit: {
      type: String,
      required: true
    },
    units: {
      type: Array as () => MultipleUnitsItem[],
      required: true
    },
    printingCapacity: {
      type: Number,
      default: undefined
    }
  },
  data(): {
    inputQuantity: number | string
    errorMessages: Array<string>
    debouncedEmitChangeQuantity: () => void
  } {
    return {
      inputQuantity: this.quantity,
      errorMessages: [],
      debouncedEmitChangeQuantity: undefined
    }
  },
  computed: {
    hasErrors(): boolean {
      return this.errorMessages.length > 0
    },
    isOutOfPrintingCapacity(): boolean {
      return this.printingCapacity === 0
    },
    inputQuantityInteger(): number {
      if (typeof this.inputQuantity === 'string') {
        if (/\d+/.test(this.inputQuantity)) {
          return Number(this.inputQuantity)
        } else {
          return NaN
        }
      }
      return this.inputQuantity
    },
    isInputLessThanMin(): boolean {
      return this.inputQuantityInteger <= this.minQuantity
    },
    isInputMoreThanMax(): boolean {
      return this.inputQuantityInteger >= this.maxQuantity
    },
    isInputLessThanSmallestRange(): boolean {
      return this.inputQuantityInteger < this.boundaryUnits[0]
    },
    boundaryUnits(): Array<number> {
      return this.units.map(item => {
        return item.quantity_greater_than
      })
    },
    inputQuantityUnitSlab(): number {
      // sortedIndex: Uses a binary search to determine the latest index
      // at which a value should be inserted into a given sorted array
      // in order to maintain the sort order of the array.
      // The return value of this computed property is the index at which
      // the input value should fit in boundaryUnits.
      // e.g: units = [50 100 200 500 1000],
      // input: 200, -> returns units[2] (200)
      // input: 190, -> returns units[1] (100)
      // input: 210, -> returns units[2] (200)
      return sortedLastIndex(this.boundaryUnits, this.inputQuantityInteger)
    },
    unitNum() :number {
      if (this.isInputLessThanSmallestRange) return this.units[0].unit_num
      return this.units[this.inputQuantityUnitSlab - 1].unit_num
    },
    isReadOnly(): boolean {
      return this.minQuantity === this.maxQuantity
    }
  },
  watch: {
    // Update input quantity if quantity is changed outside this component
    quantity(): void {
      this.inputQuantity = this.quantity
      this.validateQuantity(this.inputQuantity)
      this.emitCheckValidation()
    },
    comKey() {
      this.validateQuantity(this.inputQuantityInteger)
      this.emitCheckValidation()
    }
  },
  mounted() {
    this.validateQuantity(this.inputQuantityInteger)
  },
  created(): void {
    this.debouncedEmitChangeQuantity = debounce(this.emitChangeQuantity, 500)
  },
  methods: {
    changeQuantity(eventTarget: HTMLInputElement): void {
      this.inputQuantity = eventTarget.value
      this.$emit('updating-quantity')
      this.validateQuantity(this.inputQuantityInteger)
      this.debouncedEmitChangeQuantity()
    },
    increaseQuantityHandler(): void {
      this.inputQuantity = this.inputQuantityInteger + this.unitNum
      this.$emit('updating-quantity')
      this.validateQuantity(this.inputQuantityInteger)
      this.debouncedEmitChangeQuantity()
    },
    decreaseQuantityHandler(): void {
      this.inputQuantity = this.inputQuantityInteger - this.unitNum
      this.$emit('updating-quantity')
      this.validateQuantity(this.inputQuantityInteger)
      this.debouncedEmitChangeQuantity()
    },
    emitChangeQuantity(): void {
      this.$emit('change-quantity', {
        quantity: this.inputQuantityInteger,
        hasErrors: this.hasErrors
      })
    },
    emitCheckValidation(): void {
      this.$emit('check-validation', {
        hasErrors: this.hasErrors
      })
    },
    validateQuantity(quantity: number): void {
      this.errorMessages = []
      if (isNaN(quantity)) {
        this.errorMessages.push('数値で入力してください。')
        return
      } else if (quantity < this.minQuantity) {
        this.errorMessages.push(
          `${this.minQuantity}${this.quantityUnit}未満はご購入いただけません。`
        )
      } else if (quantity > this.maxQuantity) {
        this.errorMessages.push(
          `${this.maxQuantity}${this.quantityUnit}を超える数量はご購入いただけません。`
        )
      } else if (quantity % this.unitNum !== 0) {
        this.errorMessages.push(
          `${this.unitNum}${this.quantityUnit}単位でご注文ください。`
        )
      } else if (this.printingCapacity !== undefined && quantity > this.printingCapacity) {
        if (this.printingCapacity === 0) {
          this.errorMessages.push(
          'この商品の本日受付分を超えましたので、注文できません。'
        )}
        else {
          this.errorMessages.push(
          `この商品の本日受付分は残り${this.printingCapacity}枚です。`
          )
        }
      }
      return
    }
  }
})
